/**
 * Activity State (Actividad tab)
 *
 * Manages the flat per-execution Actividad list (TV1-1971): the live list of
 * Rule Executions, filter-independent per-status counts, and the per-execution
 * trace modal.
 */

import { RulesExecutionsReadmodelType } from '@bett3r-dev/teselly-domain';

/**
 * Flat Actividad status chip (TV1-1971) — counted over individual executions.
 * `running` groups the `pending` + `in-progress` statuses; the batch-derived
 * `partiallyFailed` bucket is intentionally dropped (meaningless per-execution).
 */
export type ExecutionFilter = 'all' | 'running' | 'failed' | 'completed';

/**
 * Filter-INDEPENDENT per-status execution counts (TV1-1971, slice 2). Sourced from
 * the dedicated `RuleExecutionsStatusCounts` backend query (one raw-SQL `GROUP BY`),
 * NOT derived from the loaded (server-filtered) `data.executions` window. `running`
 * folds `pending` + `in-progress`. The badges, the LiveStrip, and the page-header
 * pill all read this one source, so the running/per-bucket counts stay correct even
 * when the list chip is on `completed`/`failed`.
 */
export type ExecutionStatusCounts = {
  all: number;
  running: number;
  failed: number;
  completed: number;
};

export type ActivityState = {
  /** Core business data */
  data: {
    /**
     * Flat list of Rule Executions backing the Actividad table (TV1-1971).
     * One row per execution (not per batch) — the re-cardinalized list. Loaded
     * by `executionsSubscriptionPolicy` via `RuleExecutionsSearch` (startedAt
     * desc, account-scoped), refreshed on a debounced signal.
     */
    executions: RulesExecutionsReadmodelType[];

    /**
     * Filter-INDEPENDENT per-status counts (TV1-1971, slice 2). Loaded by
     * `executionsSubscriptionPolicy` from the `RuleExecutionsStatusCounts` query
     * on init + each debounced refetch — SEPARATE from and independent of the
     * server-filtered list fetch. Feeds the chip badges, the LiveStrip, and the
     * page-header running pill (all via the shared selectors) so counts stay
     * correct regardless of the active `ui.executionFilter`.
     */
    statusCounts: ExecutionStatusCounts;

    /** Open execution detail for the trace modal | null */
    traceExecution: RulesExecutionsReadmodelType | null;

    /**
     * DSL (rule definition) of the open trace execution's rule, fetched by the
     * trace policy from `ruleDefinitionId`. Fed to the dry-run node-graph
     * renderer so the execution trace is shown like a dry run. `null` until
     * resolved, or when the definition cannot be fetched (graceful fallback to
     * the flat-text trace). Type is `unknown` — the rule-editor validates it.
     */
    traceRuleDefinition: unknown | null;
  };

  /** View / interaction state */
  ui: {
    /**
     * Flat execution-list status chip (TV1-1971). Wired into the
     * `executionsSubscriptionPolicy` snapshot's `params.filter` (server-side
     * status filter over the existing `[ 'accountId', 'status' ]` index).
     */
    executionFilter: ExecutionFilter;

    /**
     * Coarse triggering-event-TYPE chip (TV1-1971, slice 3). `null` = no trigger
     * filter. Composes (AND) with `executionFilter` and `batchIdFilter` into the
     * `executionsSubscriptionPolicy` snapshot's `params.filter` — server-side over
     * the `[ 'accountId', 'triggeringEventName', 'startedAt' ]` index. Filters "all
     * OrderPlaced" (a distinct `triggeringEventName`).
     */
    triggeringEventNameFilter: string | null;

    /**
     * Occurrence DRILL (TV1-1971, slice 3) — the useful half of the retired
     * drawer. Set by clicking a row's triggering-event field to drill to that
     * trigger's sibling executions (same `batchId`). `null` = no drill. Composes
     * (AND) with the other filters via `params.filter`, server-side over the
     * existing `[ 'accountId', 'batchId' ]` index.
     */
    batchIdFilter: string | null;

    /**
     * Coarse entity hierarchy-LEVEL chip (TV1-1971, slice 4). `null` = no level
     * filter. Composes (AND) with `executionFilter`, `triggeringEventNameFilter`,
     * `batchIdFilter`, and `targetEntityIdFilter` into the
     * `executionsSubscriptionPolicy` snapshot's `params.filter` — server-side over
     * the `[ 'accountId', 'hierarchyLevel', 'startedAt' ]` index. Filters "all
     * account-level executions" (a distinct `hierarchyLevel`). Mirrors the coarse
     * triggering-event-type chip.
     */
    hierarchyLevelFilter: string | null;

    /**
     * Entity DRILL (TV1-1971, slice 4) — mirrors the occurrence drill. Set by
     * clicking a row's entity to drill to that entity's executions (same
     * `targetEntityId`). `null` = no drill. Composes (AND) with the other filters
     * via `params.filter`, server-side over the existing
     * `[ 'accountId', 'targetEntityId', 'startedAt' ]` index.
     */
    targetEntityIdFilter: string | null;

    /** List pagination (1-based) */
    page: number;

    /**
     * Pending trace target executionId | null. Set by `ui/openTrace` so the
     * trace policy can resolve the full execution row (the discarded command
     * payload is otherwise invisible to policies). Cleared by `ui/closeTrace`.
     */
    traceExecutionId: string | null;
  };

  /** Loading states — one per async operation */
  loading: {
    executionsLoading: boolean;
    traceLoading: boolean;

    /** Per-execution retry in-flight, keyed by executionId */
    retrying: Record<string, boolean>;
  };
};

export const initialState: ActivityState = {
  data: {
    executions: [],
    statusCounts: { all: 0, running: 0, failed: 0, completed: 0 },
    traceExecution: null,
    traceRuleDefinition: null
  },
  ui: {
    executionFilter: 'all',
    triggeringEventNameFilter: null,
    batchIdFilter: null,
    hierarchyLevelFilter: null,
    targetEntityIdFilter: null,
    page: 1,
    traceExecutionId: null
  },
  loading: {
    executionsLoading: false,
    traceLoading: false,
    retrying: {}
  }
};
